import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { APIListingResponseInterface } from '@app/core/_common/model';
import { environment } from '@app/env-service';

import { ClientShippingAddress } from '../store/client.model';

@Injectable({
  providedIn: 'root',
})
export class ClientShippingAddressService {
  url :string = environment.urlV2 + '/b2b-portal/clients';

  private readonly _clientShippingAddress = new BehaviorSubject<
    ClientShippingAddress[]
  >(<ClientShippingAddress[]>[]);
  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);

  clientShippingAddress$ = this._clientShippingAddress.asObservable();
  loading$ = this._loading.asObservable();

  activeBusinessAccountId :number = 0;

  constructor(private http :HttpClient) {}

  get(businessAccountId :number, params :any) {
    if (
      !this.activeBusinessAccountId ||
      businessAccountId != this.activeBusinessAccountId
    ) {
      this._clientShippingAddress.next([]);
      this.activeBusinessAccountId = businessAccountId;
    }
    this._loading.next(true);

    return this.http
      .get(`${this.url}/${businessAccountId}/shipping-addresses`, {
        params: params,
      })
      .subscribe((resp :any) => {
        let data = null;
        if (resp.data && resp.data.length > 0) {
          data = resp.data;
        }
        if (resp && resp.length > 0) {
          data = resp;
        }

        if (data) {
          const shippingAddresses :ClientShippingAddress[] = data.map(
            (shippingAddress :ClientShippingAddress) => {
              return {
                ...shippingAddress,
                fullName: this.getFullName(shippingAddress),
                addressText: this.getAddressText(shippingAddress),
              };
            },
          );
          this._clientShippingAddress.next(shippingAddresses);
        }
        this._loading.next(false);
      });
  }

  getV2(data :{ businessAccountId :number; params :any }) :Observable<any> {
    const { businessAccountId, params } = data;

    return this.http.get(`${this.url}/${businessAccountId}/shipping-addresses`, {
      params,
    });
  }

  create(
    businessAccountId :number,
    address :ClientShippingAddress,
  ) :Observable<any> {
    this._loading.next(true);

    return this.http
      .post(`${this.url}/${businessAccountId}/shipping-addresses`, address)
      .pipe(
        map((response :ClientShippingAddress) => {
          const shippingAddresses :ClientShippingAddress[] =
            this._clientShippingAddress.getValue();
          shippingAddresses.push({
            ...response,
            fullName: this.getFullName(response),
            addressText: this.getAddressText(response),
          });
          this._clientShippingAddress.next(shippingAddresses);
          this._loading.next(false);

          return response;
        }),
        catchError((response) => {
          this._loading.next(false);

          return throwError(response.error);
        }),
      );
  }

  update(
    businessAccountId :number,
    addressId :number,
    address :ClientShippingAddress,
  ) :Observable<any> {
    return this.http
      .put(
        `${this.url}/${businessAccountId}/shipping-addresses/${addressId}`,
        address,
      )
      .pipe(
        map((response :ClientShippingAddress) => {
          const shippingAddresses :ClientShippingAddress[] =
            this._clientShippingAddress.getValue();
          const idx :number = shippingAddresses.findIndex(
            (sp :ClientShippingAddress) => sp.id == addressId,
          );
          if (idx >= 0) {
            shippingAddresses.splice(idx, 1, {
              ...response,
              fullName: this.getFullName(response),
              addressText: this.getAddressText(response),
            });
          }
          this._clientShippingAddress.next(shippingAddresses);
          this._loading.next(false);

          return response;
        }),
        catchError((response) => {
          this._loading.next(false);

          return throwError(response.error);
        }),
      );
  }

  delete(businessAccountId :number, addressId :number) {
    return this.http
      .delete(`${this.url}/${businessAccountId}/shipping-addresses/${addressId}`)
      .pipe(
        map((response :ClientShippingAddress) => {
          const shippingAddresses :ClientShippingAddress[] =
            this._clientShippingAddress.getValue();
          const idx :number = shippingAddresses.findIndex(
            (sp :ClientShippingAddress) => sp.id == addressId,
          );
          if (idx >= 0) {
            shippingAddresses.splice(idx, 1);
          }
          this._clientShippingAddress.next(shippingAddresses);
          this._loading.next(false);

          return response;
        }),
        catchError((response) => {
          this._loading.next(false);

          return throwError(response.error);
        }),
      );
  }

  setDefault(businessAccountId :number, addressId :number) :Observable<any> {
    this._loading.next(true);

    return this.http
      .put(
        `${this.url}/${businessAccountId}/shipping-addresses/${addressId}/default`,
        {},
      )
      .pipe(
        map((response :ClientShippingAddress) => {
          const shippingAddresses :ClientShippingAddress[] =
            this._clientShippingAddress.getValue();
          shippingAddresses.map(
            (sp :ClientShippingAddress) => (sp.default = sp.id == addressId),
          );
          this._clientShippingAddress.next(shippingAddresses);
          this._loading.next(false);

          return response;
        }),
        catchError((response) => {
          this._loading.next(false);

          return throwError(response.error);
        }),
      );
  }

  getFullName(shipping :ClientShippingAddress) :string {
    return `${shipping.firstName} ${shipping.lastName}`;
  }

  getAddressText(shipping :ClientShippingAddress) :string {
    return `${shipping.address}, ${
      shipping.addressLine2 ? shipping.addressLine2 : ''
    }, ${shipping.city}, ${shipping.state} ${shipping.postalCode} ${
      shipping.country
    }`;
  }
}
